import React from 'react';
import clsx from 'clsx';
import moment from 'moment';

import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import Divider from '@material-ui/core/Divider';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles } from '@material-ui/core/styles';
import AccessAlarmIcon from '@material-ui/icons/AccessAlarm';
import DateRangeIcon from '@material-ui/icons/DateRange';
import { Calendar } from '@material-ui/pickers';

import { DATE_FORMAT } from 'constants/config';

import TextField from './TextField';

const useStyles = makeStyles((theme) => ({
  modalPaper: {
    margin: 8,
    width: 300,
    maxWidth: 'none !important'
  },
  modalHeader: {
    backgroundColor: '#3f51b5'
  },
  modalHeaderTitle: {
    padding: theme.spacing(2),
    lineHeight: 1,
    color: '#efefef',
    display: 'flex',
    fontSize: 20,
    justifyContent: 'flex-end',
    flexDirection: 'column',
    alignItems: 'flex-end'
  },
  modalHeaderTitleTime: {
    fontSize: 32
  },
  modalHeaderTypeSelect: {
    display: 'flex'
  },
  selectBtn: {
    flex: 1,
    color: '#efefef',
    borderRadius: 0,
    position: 'relative'
  },
  selectBtnActive: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    background: '#8a1c4a',
    height: 5
  },
  contentDividers: {
    padding: theme.spacing(1)
  },
  actionsWrap: {
    justifyContent: 'space-between'
  }
}));

export const DatePickerModal = (props) => {
  const {
    isOpen,
    handleClose = () => null,
    onChange = () => null,
    confirmText = 'Готово',
    cancelText = 'Отмена',
    resetText = 'Сбросить',
    clearable,
    dateType,
    value
  } = props;

  const classes = useStyles();

  const isDate = dateType === 'datetime' || dateType === 'date';
  const isTime = dateType === 'datetime' || dateType === 'time';

  const [tmpVal, setTmpValue] = React.useState(
    value ? value.clone() : moment()
  );
  const [selectedType, setSelectedType] = React.useState(
    !isDate ? 'time' : 'date'
  );

  React.useEffect(() => {
    setTmpValue(value ? value.clone() : moment());
  }, [value]);

  const onChangeTime = (v) => {
    setTmpValue(v);
  };

  const onChangeDate = (v) => {
    setTmpValue(v);
    if (isTime) setSelectedType('time');
  };

  const handleClear = () => {
    onChange(null);
    handleClose();
  };

  const handleSuccess = () => {
    onChange(tmpVal);
    handleClose();
  };

  return (
    <Dialog
      open={isOpen}
      scroll={'body'}
      aria-labelledby="dialog-title"
      aria-describedby="dialog-description"
      classes={{ paper: classes.modalPaper }}>
      <div className={classes.modalHeader}>
        <div className={classes.modalHeaderTitle}>
          {isDate && <div>{tmpVal.format(`dd, ${DATE_FORMAT}`)}</div>}
          {isTime && (
            <div className={classes.modalHeaderTitleTime}>
              {tmpVal.format('HH:mm')}
            </div>
          )}
        </div>
        {dateType === 'datetime' && (
          <>
            <Divider />
            <div className={classes.modalHeaderTypeSelect}>
              <IconButton
                aria-label="date type"
                className={classes.selectBtn}
                onClick={() => setSelectedType('date')}>
                <DateRangeIcon fontSize="inherit" />
                {selectedType === 'date' && (
                  <div className={classes.selectBtnActive}></div>
                )}
              </IconButton>
              <IconButton
                aria-label="time type"
                className={classes.selectBtn}
                onClick={() => setSelectedType('time')}>
                <AccessAlarmIcon fontSize="inherit" />
                {selectedType === 'time' && (
                  <div className={classes.selectBtnActive}></div>
                )}
              </IconButton>
            </div>
          </>
        )}
      </div>
      <DialogContent
        dividers={true}
        classes={{ dividers: classes.contentDividers }}>
        {selectedType === 'time' ? (
          <Clock date={tmpVal} onChange={(v) => onChangeTime(v)} />
        ) : (
          <Calendar date={tmpVal} onChange={(v) => onChangeDate(v)} />
        )}
      </DialogContent>
      <DialogActions classes={{ root: classes.actionsWrap }}>
        <div>
          {clearable && (
            <Button onClick={handleClear} color="primary">
              {resetText}
            </Button>
          )}
        </div>
        <div>
          {cancelText && handleClose && (
            <Button onClick={handleClose} color="primary">
              {cancelText}
            </Button>
          )}
          {confirmText && handleSuccess && (
            <Button onClick={handleSuccess} color="primary">
              {confirmText}
            </Button>
          )}
        </div>
      </DialogActions>
    </Dialog>
  );
};

const useClockStyles = makeStyles(() => ({
  root: {
    display: 'flex'
  },
  rootH: {
    flex: 2
  },
  rootM: {
    flex: 1
  },
  button: {
    color: '#191919',
    width: 26,
    border: '1px solid #d0d0d0',
    cursor: 'pointer',
    height: 26,
    margin: 2,
    padding: 0,
    minWidth: 0
  },
  btnActive: {
    background: '#3f51b5',
    color: '#efefef',
    '&:hover': {
      background: '#3f51b5',
      color: '#efefef'
    }
  }
}));

export const Clock = (props) => {
  const { date, onChange } = props;
  const stepM = 5;
  const classes = useClockStyles();
  const hours = Array(24).fill(1);
  const minutes = Array(60 / stepM).fill(1);

  const [h, setH] = React.useState('');
  const [m, setM] = React.useState('');

  React.useEffect(() => {
    setH(date ? date.get('h') : '');
    setM(date ? date.get('m') : '');
  }, [date]);

  const onChangeH = (v) => onChange(date.clone().hour(v));
  const onChangeM = (v) => onChange(date.clone().minute(v));

  const onChangeInputH = (e) => {
    const value = e.currentTarget.value;
    if (!value) {
      setH('');
      return;
    }
    if (/^\d{1,2}$/.test(value) && value < 24 && value >= 0) onChangeH(value);
  };

  const onChangeInputM = (e) => {
    const value = e.currentTarget.value;
    if (!value) {
      setM('');
      return;
    }
    if (/^\d{1,2}$/.test(value) && value < 60 && value >= 0) onChangeM(value);
  };

  return (
    <div className={classes.root}>
      <div className={classes.rootH}>
        <div style={{ marginBottom: 5 }}>
          <TextField
            label="Часы"
            value={h}
            type="number"
            onChange={onChangeInputH}
          />
        </div>
        <div>
          {hours.map((_, i) => {
            const title = i < 10 ? '0' + i : i;
            return (
              <Button
                key={title}
                onClick={() => onChangeH(i)}
                className={clsx(classes.button, {
                  [classes.btnActive]: i === h
                })}>
                {title}
              </Button>
            );
          })}
        </div>
      </div>
      <Divider orientation="vertical" />
      <div className={classes.rootM}>
        <div style={{ marginBottom: 5 }}>
          <TextField
            label="Минуты"
            value={m}
            type="number"
            onChange={onChangeInputM}
          />
        </div>
        <div>
          {minutes.map((_, i) => {
            const minute = i * stepM;
            const title = minute < 10 ? '0' + minute : minute;
            return (
              <Button
                key={title}
                onClick={() => onChangeM(minute)}
                className={clsx(classes.button, {
                  [classes.btnActive]: m === minute
                })}>
                {title}
              </Button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
